'use client';

import * as React from 'react';
import type { Room } from 'livekit-client';
import {
  setCameraEnabledWithRetry,
  setMicrophoneEnabledWithRetry,
} from '@/lib/cameraPublishRetry';

const PATCHED = Symbol('evoluttoMediaPatched');

/**
 * Intercepta setCameraEnabled / setMicrophoneEnabled (ControlBar TrackToggle, atalhos)
 * para usar retry + constraints relaxadas — reduz "Invalid constraint" / falha ao ligar
 * câmera ou microfone em mobile e desktop.
 */
export function useReliableCameraEnable(
  room: Room | undefined,
  onError?: (error: Error) => void,
): void {
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  React.useEffect(() => {
    if (!room) return;

    const lp = room.localParticipant as typeof room.localParticipant & {
      [PATCHED]?: boolean;
    };
    if (lp[PATCHED]) return;

    const originalCamera = lp.setCameraEnabled.bind(lp);
    const originalMic = lp.setMicrophoneEnabled.bind(lp);
    lp[PATCHED] = true;

    lp.setCameraEnabled = async (enabled, captureOptions, publishOptions) => {
      if (!enabled) {
        return originalCamera(false, captureOptions, publishOptions);
      }
      try {
        return await setCameraEnabledWithRetry(lp, true, {
          logLabel: 'camera-toggle',
          captureOptions,
          publishOptions,
          invoke: originalCamera,
        });
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        onErrorRef.current?.(err);
        throw err;
      }
    };

    lp.setMicrophoneEnabled = async (enabled, captureOptions, publishOptions) => {
      if (!enabled) {
        return originalMic(false, captureOptions, publishOptions);
      }
      try {
        return await setMicrophoneEnabledWithRetry(lp, true, {
          logLabel: 'mic-toggle',
          captureOptions,
          publishOptions,
          invoke: originalMic,
        });
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        onErrorRef.current?.(err);
        throw err;
      }
    };

    return () => {
      lp.setCameraEnabled = originalCamera;
      lp.setMicrophoneEnabled = originalMic;
      delete lp[PATCHED];
    };
  }, [room]);
}
